import { computed, reactive, ref, type ComputedRef } from 'vue'

import type { ExecutePayloadInput } from '../models/execute.types'
import type { InsightConfig } from '../models/insightConfig.types'
import type { InsightExecutionForm } from '../models/workbench.types'
import {
  buildPreviewRequestSignature,
  createInsightExecutionForm,
  hasExecutionConfig,
  toExecutePayload,
} from '../service/insightWorkbench'

export interface UseInsightExecute {
  form: InsightExecutionForm
  open: ComputedRef<boolean>
  validationKey: ComputedRef<string | null>
  signature: ComputedRef<string>
  show: () => void
  close: () => void
  setOpen: (value: boolean) => void
  setConfig: (config: InsightConfig | null) => void
  payload: (dryRun: boolean) => ExecutePayloadInput
  validate: () => boolean
  reset: () => void
}

export function useInsightExecute(): UseInsightExecute {
  const form = reactive<InsightExecutionForm>(createInsightExecutionForm())
  const isOpen = ref(false)
  const validationKey = ref<string | null>(null)
  const payload = (dryRun: boolean): ExecutePayloadInput => toExecutePayload(form, dryRun)

  function reset(): void {
    Object.assign(form, createInsightExecutionForm())
    validationKey.value = null
    isOpen.value = false
  }

  function setConfig(config: InsightConfig | null): void {
    form.config_id = config?.id ?? null
    form.config_enum = config?.enum ?? ''
    validationKey.value = null
  }

  function validate(): boolean {
    if (hasExecutionConfig(payload(true))) {
      validationKey.value = null
      return true
    }
    validationKey.value = 'insights.sysadmin.execute.configRequired'
    return false
  }

  return {
    form,
    open: computed(() => isOpen.value),
    validationKey: computed(() => validationKey.value),
    signature: computed(() => buildPreviewRequestSignature(payload(true))),
    show: () => {
      isOpen.value = true
    },
    close: () => {
      isOpen.value = false
    },
    setOpen: (value) => {
      isOpen.value = value
    },
    setConfig,
    payload,
    validate,
    reset,
  }
}
